import { useContext } from "react";
import { isMobile } from "react-device-detect";
import { toast } from "sonner";
import { buildRoute } from "routes";
import { IntlContext } from "context";
import { Button } from "components/ui/button";
import { AdInterface } from "types/ad";
import { Share2 } from "lucide-react";

interface ShareAdButtonProps {
    ad: AdInterface;
}

interface ShareNavigator {
    clipboard?: Pick<Clipboard, "writeText">;
    share?: Navigator["share"];
}

interface ShareAdOptions {
    copySuccessMessage: string;
    mobile?: boolean;
    origin?: string;
    publicUrl?: string;
    shareNavigator?: ShareNavigator;
}

export const buildAdUrl = (
    ad: AdInterface,
    origin = window.location.origin,
    publicUrl = process.env.PUBLIC_URL ?? "",
) => `${origin}${publicUrl}${buildRoute.ad(ad.id)}`;

const isAbortError = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

export const shareAd = async (ad: AdInterface, options: ShareAdOptions) => {
    const mobile = options.mobile ?? isMobile;
    const shareNavigator = options.shareNavigator ?? navigator;
    const url = buildAdUrl(ad, options.origin, options.publicUrl);

    if (mobile && shareNavigator.share) {
        await shareNavigator.share({
            title: ad.title,
            url,
        });
        return;
    }

    if (!shareNavigator.clipboard) {
        throw new Error("Clipboard API is unavailable");
    }

    await shareNavigator.clipboard.writeText(url);
    toast.success(options.copySuccessMessage);
};

const ShareAdButton: React.FC<ShareAdButtonProps> = ({ ad }) => {
    const intl = useContext(IntlContext);
    const shareLabel = intl.formatMessage("ad.view.share");

    const handleShare = async () => {
        try {
            await shareAd(ad, {
                copySuccessMessage: intl.formatMessage("ad.view.share_copied"),
            });
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }

            toast.error(intl.formatMessage("ad.view.share_copy_error"));
        }
    };

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="w-12 h-12 rounded-[14px] bg-slate-50 border border-[#f1f5f9] hover:bg-[#f1f5f9] flex items-center justify-center text-[#5a607c] transition"
            title={shareLabel}
            aria-label={shareLabel}
            onClick={handleShare}
        >
            <Share2 aria-hidden="true" className="w-5 h-5" />
        </Button>
    );
};

export default ShareAdButton;
